"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState, type ReactNode } from "react";
import useFramerMotion, { LazyMotion, m, useMotionValue, useSpring, useReducedMotion } from "@/hooks/useFramerMotion";
import { magneticOffset } from "@/utils/magnetic";
import { cn } from "@/utils";

// Tunable calibration knobs (medium & smooth feel).
const MAX = 14; // px — max pull distance
const STRENGTH = 0.35; // fraction of the cursor offset applied
const RADIUS = 30; // px — proximity band beyond the element's edges that arms the pull
const ACTIVE_Z = 20; // z-index while active, so a pulled button rides over its neighbours
const SPRING = {
  stiffness: 150,
  damping: 15,
  mass: 0.1
};
const Magnetic = t0 => {
  const $ = _c(26);
  const {
    children,
    strength: t1,
    max: t2,
    radius: t3,
    className: t4
  } = t0;
  const strength = t1 === undefined ? STRENGTH : t1;
  const max = t2 === undefined ? MAX : t2;
  const radius = t3 === undefined ? RADIUS : t3;
  const className = t4 === undefined ? "" : t4;
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const [finePointer, setFinePointer] = useState(false);
  const framerMotionFeatures = useFramerMotion();
  const xTarget = useMotionValue(0);
  const yTarget = useMotionValue(0);
  const x = useSpring(xTarget, SPRING);
  const y = useSpring(yTarget, SPRING);
  const zIndex = useMotionValue(0);
  let t5;
  let t6;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
      const update = () => setFinePointer(mq.matches);
      update();
      mq.addEventListener("change", update);
      return () => mq.removeEventListener("change", update);
    };
    t6 = [];
    $[0] = t5;
    $[1] = t6;
  } else {
    t5 = $[0];
    t6 = $[1];
  }
  useEffect(t5, t6);
  const enabled = finePointer && !reduce;
  let t7;
  let t8;
  if ($[2] !== enabled || $[3] !== max || $[4] !== radius || $[5] !== strength || $[6] !== x || $[7] !== xTarget || $[8] !== y || $[9] !== yTarget || $[10] !== zIndex) {
    t7 = () => {
      const reset = () => {
        xTarget.set(0);
        yTarget.set(0);
        zIndex.set(0);
      };
      if (!enabled) {
        reset();
        return;
      }
      const onMove = e => {
        const el = ref.current;
        if (!el) {
          return;
        }
        const rect = el.getBoundingClientRect();
        const cx = rect.left + rect.width / 2 - x.get();
        const cy = rect.top + rect.height / 2 - y.get();
        const dx = e.clientX - cx;
        const dy = e.clientY - cy;
        const inZone = Math.abs(dx) <= rect.width / 2 + radius && Math.abs(dy) <= rect.height / 2 + radius;
        if (inZone) {
          const off = magneticOffset(dx, dy, strength, max);
          xTarget.set(off.x);
          yTarget.set(off.y);
          zIndex.set(ACTIVE_Z);
        } else {
          reset();
        }
      };
      window.addEventListener("pointermove", onMove);
      document.addEventListener("mouseleave", reset);
      window.addEventListener("blur", reset);
      return () => {
        window.removeEventListener("pointermove", onMove);
        document.removeEventListener("mouseleave", reset);
        window.removeEventListener("blur", reset);
      };
    };
    t8 = [enabled, radius, strength, max, x, y, xTarget, yTarget, zIndex];
    $[2] = enabled;
    $[3] = max;
    $[4] = radius;
    $[5] = strength;
    $[6] = x;
    $[7] = xTarget;
    $[8] = y;
    $[9] = yTarget;
    $[10] = zIndex;
    $[11] = t7;
    $[12] = t8;
  } else {
    t7 = $[11];
    t8 = $[12];
  }
  useEffect(t7, t8);
  let t9;
  if ($[13] !== className) {
    t9 = cn("relative inline-block", className);
    $[13] = className;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== x || $[16] !== y || $[17] !== zIndex) {
    t10 = {
      x,
      y,
      zIndex
    };
    $[15] = x;
    $[16] = y;
    $[17] = zIndex;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== children || $[20] !== t10 || $[21] !== t9) {
    t11 = <m.div ref={ref} className={t9} style={t10}>{children}</m.div>;
    $[19] = children;
    $[20] = t10;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== framerMotionFeatures || $[24] !== t11) {
    t12 = <LazyMotion features={framerMotionFeatures}>{t11}</LazyMotion>;
    $[23] = framerMotionFeatures;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  return t12;
};
Magnetic.displayName = "Magnetic";
export default Magnetic;